import { useQuery } from '@apollo/client';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { PRIVATE_URIS } from '../../../constants/privateURIs.constants';
import { sectionIDs } from '../../../constants/section-ids.constants';
import {
  formatJoinDate,
  formatRelativeDay,
} from '../../../helpers/profileMeta';
import { useLiveReputation } from '../../../hooks/useLiveReputation';
import { useMentorships } from '../../../hooks/useMentorships';
import type { UserState } from '../../../interfaces/user.interface';
import { GET_HOVERCARD_PROFILE } from '../../Hovercard/hovercardProfileQuery';
import { VouchingCreditsStrip } from '../../Vouch/VouchingCreditsStrip';
import { ProfileOverview } from '../ProfileOverview';

type URGReputation = {
  source: string;
  overallScore?: number;
  domains?: string[];
  dimensionScores?: Record<string, number>;
  dimensionConfidences?: Record<string, number>;
  evidence?: Record<string, string[]>;
};

type OwnUser = {
  id?: string;
  username?: string;
  avatar?: string;
  role?: string;
  createdAt?: string | Date;
  lastActive?: string | Date;
  interests?: string[];
  // TRIL-613: a COUNT from the server. This was `posts?.length`, and `posts` was always empty —
  // the child resolver keyed the GSI by handle where it is keyed by userId, so every profile
  // showed 0 posts. Typing it as a count rather than a list also keeps the fix from silently
  // regressing: nothing here can go back to reading a list without changing this line.
  postCount?: number;
  userProfile?: { displayName?: string };
  userPreferences?: { avatarUrl?: string };
  reputation?: URGReputation | null;
};

interface ProfileViewProps {
  username: string;
}

export const ProfileView = ({ username }: ProfileViewProps) => {
  const user = useSelector(
    (state: UserState) => state.userStore.user as unknown as OwnUser | null,
  );
  const vouchingEnabled = import.meta.env.VITE_ENABLE_VOUCHING === 'true';

  const { data } = useQuery(GET_HOVERCARD_PROFILE, {
    variables: { userId: user?.id ?? '' },
    skip: !user?.id,
    fetchPolicy: 'cache-first',
  });
  const counts = data?.getHovercardProfile;

  const { mentorships } = useMentorships();

  // TRIL-346: keep the score live when the owner publishes/replies (fires
  // reputation.updated) without needing a reload.
  const reputation = useLiveReputation(user?.id, user?.reputation ?? null);

  return (
    <section id={sectionIDs.PROFILE_VIEW}>
      <ProfileOverview
        username={username}
        displayName={user?.userProfile?.displayName ?? null}
        avatar={user?.userPreferences?.avatarUrl ?? user?.avatar ?? null}
        role={user?.role}
        followerCount={counts?.followerCount ?? null}
        followingCount={counts?.followingCount ?? null}
        joinLabel={formatJoinDate(user?.createdAt)}
        lastActiveLabel={formatRelativeDay(user?.lastActive)}
        reputation={reputation}
        domains={reputation?.domains ?? []}
        interests={user?.interests ?? []}
        interestsHref={`/user/${username}/interests`}
        postCount={user?.postCount ?? 0}
        vouchesGiven={vouchingEnabled ? mentorships.length : null}
        vouchingSlot={
          vouchingEnabled ? (
            <>
              <VouchingCreditsStrip />
              <Link className="profile-vouching__link" to={PRIVATE_URIS.VOUCH}>
                Vouching →
              </Link>
            </>
          ) : undefined
        }
      />
    </section>
  );
};
